import type { Bundle } from '@versesverse/schema/types';

/*
 * THE MAP, TURNED INTO SOMETHING A DRAW LOOP CAN READ WITHOUT THINKING — for either renderer.
 *
 * Everything a frame needs is worked out ONCE here: a node's colour and shape, an edge's width. The
 * draw loop — Canvas 2D on the web, Skia on the phone — never rebuilds a colour string or re-derives
 * a shape per frame. That per-frame string-building was one of the diagnosed causes of the old
 * page's jank (ARCHITECTURE.md §Phase 3); the fix begins by not doing it.
 *
 * It lives in core, not in either app, because it is the same shaping for both: a colour and a
 * shape are the map, not a property of the surface it is drawn on. This is the "two thin renderers
 * over one brain" bet — a second copy in the phone app is exactly the drift core exists to prevent.
 * No canvas, no React, no DOM: pure shaping of data, positions already precomputed in `bundle.layout`.
 */

export type NodeShape = 'circle' | 'square' | 'diamond';

/** One circle on the map, everything it draws with settled. */
export interface RenderNode {
  id: string;
  label: string;
  x: number;
  y: number;
  r: number;
  /** The dominant corpus's colour, resolved once. */
  color: string;
  shape: NodeShape;
  type: string;
  /**
   * WHETHER ANYBODY HAS CHECKED THIS FIGURE — drawn dimmer when nobody has, and filterable.
   *
   * It was `auto`, meaning MACHINE-FOUND, and its own comment read "machine-found rather than
   * checked" — two different questions welded into one word, which is the trap this repository
   * keeps paying for. They are not the same set. Measured 2026-08-22 over 2,409 figures:
   *
   *     1,568   machine-found, and none of them checked
   *       208   entered by hand, and NOT checked either
   *       633   checked — universal, same-name-only, or contested
   *
   * So dimming by `auto` drew those 208 at full strength, saying the roster was sure of a figure
   * nobody had looked at. The record of what has been checked is `agreement`, which the bundle has
   * carried all along, and this is now read from it — one question, one field, both renderers.
   *
   * A missing `agreement` counts as unchecked. The honest direction for an absent record: silence
   * is not a verification.
   */
  checked: boolean;
  corpora: string[];
  /**
   * The colour of each text that names this figure, in registry order — the arc ring drawn around
   * a shared disc, which says WHICH scriptures at a glance rather than only how many.
   *
   * Resolved here rather than in the renderer because both renderers would otherwise carry the
   * palette and the lookup, which is the drift core exists to prevent; and because the alternative
   * is a per-frame map lookup per corpus per node, on a path that runs 1,488 times a frame.
   */
  corpusColors: string[];
  /**
   * Named across EVERY tradition the library carries — the map's rarest mark.
   *
   * Not "named by three or more scriptures", which is the thing that first suggests itself and is
   * wrong in a way that matters: this library carries four Jewish texts, three Christian and two
   * Islamic, so a figure in the Hebrew Bible, the Mishnah and the Talmud would score three and be
   * held by ONE tradition. The map would then mark a purely Jewish figure as one the traditions
   * share, which is the opposite of what the mark means. Counting distinct traditions is immune to
   * how many books each of them happens to contribute.
   */
  shared: boolean;
  /** Whether it is disputed that this is one figure across the scriptures — the map rings it in gold. */
  contested?: boolean;
  /**
   * The rung the layout was asked to place this name on — 0 is nearest the divine.
   *
   * Carried rather than derived from `r` or from the distance out, because those are the settle's
   * ANSWER and this is its question. A name pushed off its ring by a dense crowd is still of the
   * rung it belongs to, and an interface that reads the rung off the radius would tell a reader
   * they had crossed into somewhere they had not.
   */
  tier: number;
}

/** One line between two nodes, by their INDEX in the node array — a number beats a map lookup per frame. */
export interface RenderEdge {
  a: number;
  b: number;
  width: number;
}

/*
 * The map keeps a deliberately small visual language: three shapes, coloured by scripture, not by
 * kind. So the new kinds join the existing shapes by their nature rather than minting new marks that
 * would clutter the field — a being is a disc, a thing that sits in the world is a square, a body of
 * many is a diamond. The precise kind (object vs place, angel vs person) is told in the drawer, where
 * there is room to say it; the map stays legible at a glance. A disc is the fallback for anything new.
 */
/**
 * WHAT A NODE WEARS WHEN NO SCRIPTURE NAMES IT.
 *
 * 75 nodes, and two real groups: figures the scriptures describe and never name — the son
 * Abraham was commanded to sacrifice, Pharaoh's wife, al-Khidr, the Queen of Sheba — and
 * figures only the hadith name, which is most of what the report work has added.
 *
 * Named rather than left as a literal in the fallback, because the legend has to paint the same
 * grey and a swatch that only approximately matches the field is a key nobody can line up.
 */
export const NO_SCRIPTURE_COLOUR = '#888888';

export const SHAPE_BY_TYPE: Record<string, NodeShape> = {
  // beings — a disc
  person: 'circle',
  divine: 'circle',
  angelic: 'circle',
  deity: 'circle',
  unknown: 'circle',
  // things that sit in the world — a square
  place: 'square',
  object: 'square',
  scripture: 'square',
  language: 'square',
  // bodies of many, and the shared life around them — a diamond
  people: 'diamond',
  institution: 'diamond',
  observance: 'diamond',
  time: 'diamond',
  /* What a community does and what it decides is shared life too — see ENTITY_KINDS. */
  practice: 'diamond',
  ruling: 'diamond',
};

/** What {@link buildNodes} reads from a node — narrower than EntityNode, which is assignable to it. */
export interface NodeInput {
  id: string;
  name: string;
  type: string;
  auto?: boolean;
  counts: Record<string, number>;
  corpora: string[];
  /** Whether a human found the cross-scripture identity disputed. `'contested'` rings the node. */
  agreement?: string;
}

/** What {@link buildNodes} reads from a corpus — its id, the colour its figures wear, and who reads it. */
export interface CorpusColour {
  id: string;
  colour: string;
  /**
   * The traditions that hold this text. Optional so a caller with only a palette still works — but
   * without it no figure can be marked as one the traditions SHARE, because sharing is a fact about
   * traditions and not about how many books happen to carry a name.
   */
  traditions?: readonly string[];
}

/**
 * HAS ANYBODY CHECKED THIS FIGURE — the one place that question is answered, for both doors.
 *
 * `agreement` is the record: `universal`, `same-name-only` and `contested` are all verdicts somebody
 * reached, and `unverified` is the absence of one. A missing value is unchecked too, because silence
 * is not a verification.
 *
 * Exported so the compilation packs the same answer it decodes — the bundle door and the skeleton
 * door used to agree only because both read `auto`, and this keeps them agreeing on purpose.
 */
export function isChecked(agreement: string | undefined): boolean {
  return agreement !== undefined && agreement !== 'unverified';
}

/** The corpus a figure is most named in — which decides its colour. Ties break by registry order. */
export function dominantCorpus(counts: Record<string, number>, order: string[]): string {
  let best = order[0] ?? '';
  let most = -1;
  for (const id of order) {
    const c = counts[id] ?? 0;
    if (c > most) { most = c; best = id; }
  }
  return best;
}

/** A line's drawn width, from the strength of the tie — precomputed, never rebuilt per frame. */
export function edgeWidth(strength: number): number {
  return Math.max(0.4, Math.min(2.5, 0.3 + Math.sqrt(strength) * 0.35));
}

/**
 * How many traditions the whole library holds — the number a figure must reach to count as SHARED.
 *
 * Counted from the registry rather than written down, so adding a tradition raises the bar by
 * itself instead of leaving a literal `3` that nothing keeps in step. Zero when the caller gave a
 * bare palette with no traditions on it, and `sharedAcross` reads that as "cannot know", never as
 * "everything qualifies" — a mark that fires on every figure is worse than one that never fires.
 */
function traditionCount(corpora: readonly CorpusColour[]): number {
  const all = new Set<string>();
  for (const c of corpora) for (const t of c.traditions ?? []) all.add(t);
  return all.size;
}

/** Whether these texts, between them, are read by every tradition the library carries. */
function sharedAcross(ids: readonly string[], byId: Map<string, CorpusColour>, total: number): boolean {
  if (total === 0) return false;
  const held = new Set<string>();
  for (const id of ids) for (const t of byId.get(id)?.traditions ?? []) held.add(t);
  return held.size >= total;
}

/** Build the draw-ready nodes from the shipped nodes, their precomputed positions, and the palette. */
export function buildNodes(
  nodeInputs: NodeInput[],
  layout: Bundle['layout'],
  corpora: CorpusColour[],
): RenderNode[] {
  const colour = new Map(corpora.map(c => [c.id, c.colour]));
  const byId = new Map(corpora.map(c => [c.id, c]));
  const traditions = traditionCount(corpora);
  const order = corpora.map(c => c.id);
  const nodes: RenderNode[] = [];
  for (const n of nodeInputs) {
    const pos = layout[n.id];
    /* Validated to exist for every node (schema/validate.ts); skipped rather than trusted, because
       a node drawn at an undefined position is a crash dressed as a dot. */
    if (!pos) continue;
    nodes.push({
      id: n.id,
      label: n.name,
      x: pos[0],
      y: pos[1],
      r: pos[2],
      color: colour.get(dominantCorpus(n.counts, order)) ?? NO_SCRIPTURE_COLOUR,
      shape: SHAPE_BY_TYPE[n.type] ?? 'circle',
      type: n.type,
      checked: isChecked(n.agreement),
      corpora: n.corpora,
      /* Registry order, so the arcs run round the disc the same way the wheel runs round the map. */
      corpusColors: order.filter(id => n.corpora.includes(id)).map(id => colour.get(id) ?? NO_SCRIPTURE_COLOUR),
      shared: sharedAcross(n.corpora, byId, traditions),
      contested: n.agreement === 'contested',
      tier: pos[3],
    });
  }
  return nodes;
}

/** Build the draw-ready edges, addressing nodes by index. A tie to a node not shown is not drawn. */
export function buildEdges(ties: Bundle['ties'], indexById: Map<string, number>): RenderEdge[] {
  const edges: RenderEdge[] = [];
  for (const t of ties) {
    const a = indexById.get(t.a);
    const b = indexById.get(t.b);
    if (a === undefined || b === undefined) continue;
    edges.push({ a, b, width: edgeWidth(t.n) });
  }
  return edges;
}

/**
 * WHERE EACH TIE IS WRITTEN — the one passage that names both figures, keyed by the pair.
 *
 * A strand between two names is an assertion, and a reader who cannot see the verse behind it is
 * being asked to take the map's word for it. `build-web-data.ts` keeps the EARLIEST passage naming
 * both, so the citation is stable across builds rather than being whichever one the loop happened
 * to see last.
 *
 * The key is the two ids SORTED and joined by `|`, because a tie has no direction and the open
 * name's fan asks about it from whichever end the reader opened. A tie with no citation is simply
 * ABSENT rather than present-and-empty: the map then draws a strand with no writing on it, which is
 * the honest picture of a connection this build could not point at a verse for.
 */
export function tieRefs(ties: Bundle['ties']): Map<string, string> {
  const refs = new Map<string, string>();
  for (const t of ties) {
    if (t.ref === undefined) continue;
    refs.set(tieKey(t.a, t.b), t.ref);
  }
  return refs;
}

/**
 * How a tie between two figures is addressed, whichever way round it is named.
 *
 * Both the building and the reading of `tieRefs` need this rule, and the second caller is where a
 * second copy of it would appear — a map keyed one way and read another answers nothing, silently,
 * for every tie.
 */
export function tieKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

/** id -> its index in `nodes`, for building edges and for hit results. */
export function indexNodes(nodes: RenderNode[]): Map<string, number> {
  return new Map(nodes.map((n, i) => [n.id, i]));
}

/*
 * THE SAME MAP, BUILT FROM THE CHUNKED ARTIFACT INSTEAD OF FROM THE BUNDLE.
 *
 * `ARCHITECTURE.md D26` moves the compilation behind the API in pieces, so the app has to be able to
 * draw from a skeleton it fetched rather than from a bundle it holds. These two are the other end of
 * `pipeline/lib/compilation.ts`, and the only thing that matters about them is that they produce what
 * `buildNodes` and `buildEdges` produce — the renderer must not be able to tell which door the data
 * came through, and a test asserts it cannot.
 *
 * The work the build already did is not redone: the colour is an index, the corpora are a mask, and
 * `contested` is a bit. That is the whole reason a row is cheaper than a record.
 */

/** What a skeleton row carries, positionally. Mirrors `SkeletonRow` in schema/artifacts.ts. */
type Row = readonly [
  id: string, x: number, y: number, r: number, name: string,
  colour: number, kind: string, checked: number, corpora: number, contested: number, tier: number,
];

/** The shape `nodesFromSkeleton` reads — narrower than the artifact, which is assignable to it. */
export interface SkeletonInput {
  readonly corpora: readonly string[];
  readonly rows: readonly Row[];
  readonly edges: readonly (readonly number[])[];
}

/** The palette, by corpus id — the same input `buildNodes` takes. */
export function nodesFromSkeleton(skeleton: SkeletonInput, corpora: CorpusColour[]): RenderNode[] {
  const colour = new Map(corpora.map(c => [c.id, c.colour]));
  const byId = new Map(corpora.map(c => [c.id, c]));
  const traditions = traditionCount(corpora);
  const order = corpora.map(c => c.id);
  return skeleton.rows.map(row => ({
    id: row[0],
    label: row[4],
    x: row[1],
    y: row[2],
    r: row[3],
    /* The build already chose which corpus decides the colour; this only looks it up. A row naming a
       corpus this palette does not have wears the no-scripture grey, exactly as `buildNodes` does. */
    color: colour.get(skeleton.corpora[row[5]] ?? '') ?? NO_SCRIPTURE_COLOUR,
    shape: SHAPE_BY_TYPE[row[6]] ?? 'circle',
    type: row[6],
    checked: row[7] === 1,
    corpora: skeleton.corpora.filter((_, bit) => (row[8] & (1 << bit)) !== 0),
    /* Registry order — `order`, not the skeleton's own list, so the arcs run the same way round the
       disc as they do through the bundle door even if the two orders ever part company. */
    corpusColors: order.filter(id => skeleton.corpora.some((c, bit) => c === id && (row[8] & (1 << bit)) !== 0))
      .map(id => colour.get(id) ?? NO_SCRIPTURE_COLOUR),
    shared: sharedAcross(skeleton.corpora.filter((_, bit) => (row[8] & (1 << bit)) !== 0), byId, traditions),
    contested: row[9] === 1,
    tier: row[10],
  }));
}

/**
 * The edges, already addressed by index and already carrying their width.
 *
 * The width arrives ×10 as an integer, because a row of integers is smaller on the wire than a row of
 * floats and the value is a stroke width — a tenth of a pixel is not a thing a reader can see.
 */
export function edgesFromSkeleton(skeleton: SkeletonInput): RenderEdge[] {
  const edges: RenderEdge[] = [];
  for (const row of skeleton.edges) {
    const [a, b, width] = row;
    /* An edge pointing outside the rows it came with is dropped rather than drawn to nowhere — the
       same refusal as `buildEdges`, and here it also means a truncated response cannot crash a frame. */
    if (a === undefined || b === undefined || a >= skeleton.rows.length || b >= skeleton.rows.length) continue;
    edges.push({ a, b, width: (width ?? 4) / 10 });
  }
  return edges;
}
